import React, { useEffect, useImperativeHandle, useRef, useState } from "react";
import styles from "./Tooltip.module.scss";
import classnames from "classnames";
import useFixedPositioningAroundElement from "../fixedPosition/FixedPositioningAroundElement";
import { TurnrPortal } from "../portal/TurnrPortal";
import _ from "lodash";
import useTurnrCallbackRef from "rylak-common/src/hooks/useTurnrCallbackRef.jsx";

const TOOLTIP_MAX_WIDTH = 500;

export function TurnrTooltipTitle({ children }) {
    return <h2 className={styles["tooltip-title"]}>{children}</h2>;
}

export function Tooltip(props) {
    const { children, portalRef, content, title, style, className, placement = "top", active = false, tight } = props;
    const [hover, setHover] = useState();
    const localPortalRef = useRef();
    const [id] = useState(_.uniqueId);

    useEffect(() => {
        const ref = portalRef || localPortalRef;
        if (ref) {
            if (hover || active) {
                ref?.current?.open({ title, content, id });
            } else if (!active) {
                ref?.current?.close(id);
            }
        }
    }, [hover, portalRef, localPortalRef, active, content, title]);
    return (
        <div onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} style={style} className={className}>
            {portalRef ? (
                children
            ) : (
                <TurnrTooltipPortalAnchor ref={localPortalRef} placement={placement} tight={tight}>
                    {children}
                </TurnrTooltipPortalAnchor>
            )}
        </div>
    );
}

function TurnrTooltipPortalAnchor(props) {
    const { children, placement = "top", style, tight, ref } = props;

    const [tooltipId, setTooltipId] = useState();
    const [content, setContent] = useState();
    const [title, setTitle] = useState();
    const [open, setOpen] = useState(false);
    const [closeRequestedId, setCloseRequestedId] = useState(null);

    useImperativeHandle(
        ref,
        () => ({
            open({ title, content, id }) {
                setTooltipId(id);
                setTitle(title);
                setContent(content);
                setOpen(true);
            },
            close(id) {
                if (open) {
                    setCloseRequestedId(id);
                }
            },
        }),
        [tooltipId, open],
    );

    useEffect(() => {
        if (closeRequestedId === tooltipId) {
            setOpen(false);
            setCloseRequestedId(null);
        }
    }, [closeRequestedId, tooltipId]);

    const contentRef = useTurnrCallbackRef(null);
    const anchorRef = useRef();

    const setFixedPositioning = useFixedPositioningAroundElement({
        placement,
        elementRef: anchorRef,
        fixedElementRef: contentRef,
    });

    useEffect(() => {
        if (contentRef?.current) {
            setFixedPositioning();
        }
    }, [open, content, title, ref, tooltipId, setFixedPositioning, contentRef?.current]);

    return (
        <div ref={anchorRef} style={{ ...style, position: "relative" }}>
            <TurnrPortal open={open}>
                <div>
                    {(title || content) && (
                        <div
                            ref={contentRef}
                            className={classnames(styles["content"], open && styles["open"], content && styles["has-content"])}
                            style={{
                                position: "fixed",
                                zIndex: 2000,
                                maxWidth: TOOLTIP_MAX_WIDTH,
                                right: 0,
                                top: 0,
                                padding: tight ? "8px" : "15px",
                                fontSize: "12px",
                                borderRadius: "5px",
                            }}
                        >
                            {title && <TurnrTooltipTitle>{title}</TurnrTooltipTitle>}
                            <div style={{ maxWidth: "500px" }}>{content}</div>
                        </div>
                    )}
                </div>
            </TurnrPortal>
            {children}
        </div>
    );
}

export { TurnrTooltipPortalAnchor };
